'use client'

import { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { X, ChevronLeft, ChevronRight, Building2 } from 'lucide-react'
import { useSwipe } from '../hooks/useSwipe'
import { optimizeStorageImage } from '@/lib/image-utils'

interface PropertyGalleryProps {
  images: Array<{ id: string; url: string; caption: string | null }>
  propertyName: string
}

export function PropertyGallery({ images, propertyName }: PropertyGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [mobileIndex, setMobileIndex] = useState(0)

  const nextMobile = useCallback(() => {
    setMobileIndex((i) => Math.min(i + 1, images.length - 1))
  }, [images.length])

  const prevMobile = useCallback(() => {
    setMobileIndex((i) => Math.max(i - 1, 0))
  }, [])

  const mobileSwipe = useSwipe({
    onSwipeLeft: nextMobile,
    onSwipeRight: prevMobile,
  })

  if (images.length === 0) {
    return (
      <div className="relative aspect-[21/9] md:aspect-[3/1] bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden flex items-center justify-center">
        <Building2 className="h-16 w-16 text-gray-300 dark:text-gray-600" />
      </div>
    )
  }

  const hero = images[0]!
  const thumbs = images.slice(1, 5)
  const hasMore = images.length > 5

  return (
    <>
      {/* Mobile: Swipeable carousel */}
      <div className="md:hidden -mx-4">
        <div
          className="relative overflow-hidden"
          {...mobileSwipe}
        >
          {/* RTL-aware slide: in dir=rtl the flex row lays out right-to-left, so
              slide i sits at -i*100% already — translating negative would move it
              further off-screen (blank carousel). The rtl: variant flips the sign. */}
          <div
            className="flex transition-transform duration-300 ease-out [transform:translateX(calc(var(--slide)*-100%))] rtl:[transform:translateX(calc(var(--slide)*100%))]"
            style={{ '--slide': mobileIndex } as React.CSSProperties}
          >
            {images.map((img, i) => (
              <button
                key={img.id}
                type="button"
                className="relative w-full shrink-0 aspect-[4/3] bg-gray-100 dark:bg-gray-800"
                onClick={() => setLightboxIndex(i)}
              >
                <Image
                  src={optimizeStorageImage(img.url, { w: 800, f: 'webp', q: 75 })}
                  alt={img.caption || `${propertyName} ${i + 1}`}
                  fill
                  className="object-cover"
                  sizes="100vw"
                  priority={i === 0}
                  fetchPriority={i === 0 ? 'high' : 'low'}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  unoptimized
                />
              </button>
            ))}
          </div>

          {/* Dot indicators */}
          {images.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-full px-2.5 py-1.5">
              {images.length <= 8 ? (
                images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`rounded-full transition-all ${
                      i === mobileIndex
                        ? 'w-2 h-2 bg-white'
                        : 'w-1.5 h-1.5 bg-white/50'
                    }`}
                    onClick={() => setMobileIndex(i)}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))
              ) : (
                <span className="text-white text-xs font-medium">
                  {mobileIndex + 1} / {images.length}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Grid layout */}
      <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden h-[420px]">
        {/* Hero image - spans 2 cols, 2 rows */}
        <button
          type="button"
          className="col-span-2 row-span-2 relative bg-gray-100 dark:bg-gray-800 cursor-pointer group"
          onClick={() => setLightboxIndex(0)}
        >
          <Image
            src={optimizeStorageImage(hero.url, { w: 1200, f: 'webp', q: 75 })}
            alt={hero.caption || propertyName}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            sizes="50vw"
            priority
            fetchPriority="high"
            unoptimized
          />
        </button>

        {/* Thumbnail grid */}
        {thumbs.map((img, i) => (
          <button
            key={img.id}
            type="button"
            className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800 cursor-pointer group"
            onClick={() => setLightboxIndex(i + 1)}
          >
            <Image
              src={optimizeStorageImage(img.url, { w: 600, f: 'webp', q: 75 })}
              alt={img.caption || `${propertyName} ${i + 2}`}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="25vw"
              unoptimized
            />
            {/* "Show all" overlay on last thumb */}
            {i === thumbs.length - 1 && hasMore && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  +{images.length - 5} more
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  )
}

function Lightbox({
  images,
  currentIndex,
  onClose,
  onNavigate,
}: {
  images: Array<{ id: string; url: string; caption: string | null }>
  currentIndex: number
  onClose: () => void
  onNavigate: (index: number) => void
}) {
  const current = images[currentIndex]!

  const goNext = useCallback(() => {
    if (currentIndex < images.length - 1) onNavigate(currentIndex + 1)
  }, [currentIndex, images.length, onNavigate])

  const goPrev = useCallback(() => {
    if (currentIndex > 0) onNavigate(currentIndex - 1)
  }, [currentIndex, onNavigate])

  const swipeHandlers = useSwipe({
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
  })

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') goNext()
      if (e.key === 'ArrowLeft') goPrev()
    }
    document.addEventListener('keydown', handleKeyDown)
    // Prevent body scroll while lightbox is open
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose, goNext, goPrev])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-label="Image gallery"
    >
      {/* Close */}
      <button
        type="button"
        className="absolute top-4 right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous */}
      {currentIndex > 0 && (
        <button
          type="button"
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); goPrev() }}
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Next */}
      {currentIndex < images.length - 1 && (
        <button
          type="button"
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); goNext() }}
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Image with swipe support */}
      <div
        className="relative w-full h-full max-w-5xl max-h-[85vh] mx-auto p-4"
        onClick={(e) => e.stopPropagation()}
        {...swipeHandlers}
      >
        <Image
          src={current.url}
          alt={current.caption || `Photo ${currentIndex + 1}`}
          fill
          className="object-contain"
          sizes="100vw"
          unoptimized
        />
      </div>

      {/* Caption */}
      {current.caption && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
          {current.caption}
        </div>
      )}
    </div>
  )
}
